import {useTranslation} from '../../utils/useTranslation'
import {theme} from '../../theme'
import {useSelectedDate, useViewMode, useUIActions} from '../../store'
import {isCurrentWeek, isCurrentDay} from '../../utils/dates'
import CalendarDayIcon from '../CalendarDayIcon'
import './styles.css'

interface TodayButtonProps {
  /** Lock the navigation unit regardless of the global view mode. */
  forceViewMode?: 'week' | 'day'
  /** Icon-only rendering for tight layouts (e.g. mobile). */
  compact?: boolean
}

/**
 * "Jump to today" reset, extracted from CalendarHeader so it reads as a distinct
 * action rather than another step. Self-contained: reads the view mode / selected
 * date from the UI store and drives the same week/day navigation actions.
 */
export default function TodayButton({
  forceViewMode,
  compact = false,
}: TodayButtonProps) {
  const {t} = useTranslation()
  const globalViewMode = useViewMode()
  const viewMode = forceViewMode ?? globalViewMode
  const selectedDate = useSelectedDate()
  const {setWeekNavigation, setDayNavigation} = useUIActions()

  const isOnToday =
    viewMode === 'week'
      ? isCurrentWeek(selectedDate)
      : isCurrentDay(selectedDate)

  const goToToday = () =>
    viewMode === 'week'
      ? setWeekNavigation('current')
      : setDayNavigation('current')

  const label = t('general:today')
  const iconColor = isOnToday
    ? theme.palette.primary.main
    : theme.palette.text.primary

  return (
    <button
      type='button'
      className={`todayButton${isOnToday ? ' active' : ''}${compact ? ' compact' : ''}`}
      onClick={goToToday}
      title={compact ? label : undefined}>
      <CalendarDayIcon
        day={new Date().getDate()}
        size='24px'
        color={iconColor}
      />
      {!compact && <span>{label}</span>}
    </button>
  )
}
